
import { useBoardCollaboration } from '@/hooks/useBoardCollaboration';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { UserX, Crown, Shield, Edit, Eye } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

interface BoardMembersTabProps {
  boardId: string;
}

export const BoardMembersTab = ({ boardId }: BoardMembersTabProps) => {
  const { user } = useAuth();
  const {
    boardMembers,
    canManageMembers,
    membersLoading,
    updateMemberRole,
    removeMember,
    isUpdatingRole,
    isRemovingMember
  } = useBoardCollaboration(boardId);

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner':
        return <Crown className="h-4 w-4 text-yellow-500" />;
      case 'admin':
        return <Shield className="h-4 w-4 text-blue-500" />;
      case 'editor':
        return <Edit className="h-4 w-4 text-green-500" />;
      case 'viewer':
        return <Eye className="h-4 w-4 text-gray-500" />;
      default:
        return null;
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'owner':
        return 'default';
      case 'admin':
        return 'secondary';
      case 'editor':
        return 'outline';
      case 'viewer':
        return 'outline';
      default:
        return 'outline';
    }
  };

  if (membersLoading) {
    return <div>Loading members...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Board Members</CardTitle>
        <CardDescription>
          Manage who has access to this board and their permissions.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {boardMembers?.map((member) => {
            const isCurrentUser = member.user_id === user?.id;
            const canManageThisMember = canManageMembers && !isCurrentUser && member.role !== 'owner';
            const profile = member.profiles;

            return (
              <div key={member.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarImage src={profile?.avatar_url || ''} />
                    <AvatarFallback className="bg-primary text-primary-foreground dark:bg-slate-600 dark:text-slate-100">
                      {profile?.full_name?.charAt(0) || 
                       profile?.username?.charAt(0) || 
                       '?'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="font-medium">
                      {profile?.full_name || profile?.username || 'Unknown User'}
                      {isCurrentUser && <span className="text-sm text-gray-500 ml-2">(You)</span>}
                    </div>
                    <div className="text-sm text-gray-500">
                      Joined {new Date(member.joined_at || member.created_at || '').toLocaleDateString()}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Badge variant={getRoleBadgeVariant(member.role)} className="flex items-center gap-1">
                    {getRoleIcon(member.role)}
                    {member.role}
                  </Badge>

                  {canManageThisMember && (
                    <div className="flex items-center gap-2">
                      <Select
                        value={member.role}
                        onValueChange={(newRole) => updateMemberRole({ memberId: member.id, role: newRole })}
                        disabled={isUpdatingRole}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="viewer">Viewer</SelectItem>
                          <SelectItem value="editor">Editor</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>

                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={isRemovingMember}
                          >
                            <UserX className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remove Member</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to remove {profile?.full_name || profile?.username || 'this user'} from this board?
                              They will lose access to all board content.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => removeMember(member.id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Remove Member
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {boardMembers?.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No members found.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
